import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Block as BlockIcon, Print as PrintIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import remitoCompraService, { RemitoCompra } from '../../services/remitoCompraService';
import ubicacionService from '../../services/ubicacionService';
import { formatDate } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import { getApiErrorMessage } from '../../utils/apiError';
import { esRemitoCompraAnulado, remitoPuedeAnular } from '../../utils/remitoPuedeAnular';
import {
  obtenerFacturasAsociadasRemito,
  RemitoFacturaAsociada,
} from '../../utils/remitoCompraFacturasAsociadas';
import PDFViewer from '../../components/shared/PDFViewer';
import ImprimirEtiquetasCodigosDialog from '../../components/compras/ImprimirEtiquetasCodigosDialog';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

/** Formato Letra-Suc-Nro: A-0001-00000001 */
const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number | string): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro =
    typeof nroComprobante === 'number'
      ? nroComprobante
      : parseInt(nroComprobante ?? '', 10);
  const safeNro = Number.isNaN(nro) ? 0 : nro;
  return `${L}-${suc}-${String(safeNro).padStart(8, '0')}`;
};

const RemitoCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [remito, setRemito] = useState<RemitoCompra | null>(null);
  const [facturasAsociadas, setFacturasAsociadas] = useState<RemitoFacturaAsociada[]>([]);
  const [dialogFacturasOpen, setDialogFacturasOpen] = useState(false);
  const [ubicacionDestinoNombre, setUbicacionDestinoNombre] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [anulando, setAnulando] = useState(false);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [pdfEtiquetas, setPdfEtiquetas] = useState<{ url: string; fileName: string } | null>(null);
  const pdfEtiquetasRef = useRef<{ url: string; fileName: string } | null>(null);

  const loadRemito = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (Number.isNaN(id)) {
      enqueueSnackbar('Remito no válido', { variant: 'error' });
      navigate('/compras/remitos');
      return;
    }

    try {
      setLoading(true);
      const data = await remitoCompraService.getRemitoCompra(id);
      setRemito(data);
      setFacturasAsociadas([]);
      setDialogFacturasOpen(false);

      try {
        const facturas = await obtenerFacturasAsociadasRemito(data);
        setFacturasAsociadas(facturas);
      } catch (e) {
        console.warn('No se pudieron cargar las facturas asociadas:', e);
        setFacturasAsociadas([]);
      }

      if (data?.nroUbicacionDestino) {
        try {
          const u = await ubicacionService.getUbicacion(data.nroUbicacionDestino);
          setUbicacionDestinoNombre(u?.nombreUbicacion?.trim() || '');
        } catch {
          setUbicacionDestinoNombre('');
        }
      } else {
        setUbicacionDestinoNombre('');
      }
    } catch (error) {
      console.error('Error al cargar remito:', error);
      enqueueSnackbar('Error al cargar el remito de compra', { variant: 'error' });
      navigate('/compras/remitos');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadRemito();
  }, [loadRemito]);

  useEffect(() => {
    const st = location.state as { pedirEtiquetas?: boolean } | null;
    if (st?.pedirEtiquetas && remito && !loading) {
      setEtiquetasOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [remito, loading, location.pathname, location.state, navigate]);

  useEffect(() => {
    pdfEtiquetasRef.current = pdfEtiquetas;
  }, [pdfEtiquetas]);

  useEffect(() => {
    return () => {
      const p = pdfEtiquetasRef.current;
      if (p?.url) URL.revokeObjectURL(p.url);
    };
  }, []);

  const handleAnular = async () => {
    if (anulando || !remito || esRemitoCompraAnulado(remito)) return;
    try {
      const check = await remitoPuedeAnular(remito.nroFormulario, remito.detalles);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivo, { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular este remito de compra?')) return;

      setAnulando(true);
      await remitoCompraService.anularRemitoCompra(remito.nroFormulario);
      enqueueSnackbar('Remito de compra anulado correctamente', { variant: 'success' });
      await loadRemito();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular el remito de compra'), { variant: 'error' });
    } finally {
      setAnulando(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!remito) return null;

  const nroComprobanteTxt = remito.nroComprobante ?? remito.nroRemito ?? '';
  const detalles = remito.detalles || [];
  const facturaUnica = facturasAsociadas.length === 1 ? facturasAsociadas[0] : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/compras/remitos')}
              sx={{ textTransform: 'none' }}
            >
              Remitos de Compra
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.2rem', sm: '2.125rem' },
                wordBreak: 'break-word',
                flex: 1,
              }}
            >
              Remito de Compra {formatoLetraSucNro(remito.letra, remito.nroSuc, nroComprobanteTxt)}
            </Typography>
            {!esRemitoCompraAnulado(remito) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => setEtiquetasOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Imprimir etiquetas
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  disabled={anulando}
                  onClick={() => void handleAnular()}
                  sx={{ textTransform: 'none' }}
                >
                  Anular
                </Button>
              </Stack>
            )}
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Fecha emisión
              </Typography>
              <Typography variant="body1">{formatearFecha(remito.fechaEmision)}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Nro Comprobante
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {formatoLetraSucNro(remito.letra, remito.nroSuc, nroComprobanteTxt)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {facturasAsociadas.length > 1 ? 'Facturas asociadas' : 'Nro. factura'}
              </Typography>
              {facturaUnica ? (
                <Link
                  component={RouterLink}
                  to={`/compras/facturas/${facturaUnica.nroFormulario}`}
                  underline="hover"
                  sx={{ color: '#1976d2', fontWeight: 500, wordBreak: 'break-word' }}
                >
                  {formatoLetraSucNro(
                    facturaUnica.letra,
                    facturaUnica.nroSuc,
                    facturaUnica.nroComprobante ?? undefined
                  )}
                </Link>
              ) : facturasAsociadas.length > 1 ? (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => setDialogFacturasOpen(true)}
                  sx={{
                    color: '#1976d2',
                    fontWeight: 500,
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                    p: 0,
                    border: 0,
                    background: 'none',
                  }}
                >
                  {facturasAsociadas.length} facturas asociadas
                </Link>
              ) : (
                <Typography variant="body1">-</Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Proveedor
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {remito.nroCliente != null ? remito.nroCliente : '-'} - {remito.nombre || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Ubicación destino
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {remito.nroUbicacionDestino != null
                  ? ubicacionDestinoNombre
                    ? `${remito.nroUbicacionDestino} - ${ubicacionDestinoNombre}`
                    : String(remito.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Usuario
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {remito.usuario || '-'}
              </Typography>
            </Grid>

            {remito.descripcion && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                  {remito.descripcion}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Detalle
          </Typography>

          {detalles.length === 0 ? (
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Sin ítems.
            </Typography>
          ) : isMobile ? (
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {detalles.map((d, idx) => (
                <Card key={`${d.nroIdformulario}-${d.nroItem}-${idx}`} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Box
                        sx={(t) => ({
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor: t.palette.primary.light + '14',
                        })}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
                          {d.codigoArticulo || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
                          {d.descripcionArticulo || '-'}
                        </Typography>
                      </Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Cantidad
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}
                        </Typography>
                      </Stack>
                      {d.notas ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Notas
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {d.notas}
                          </Typography>
                        </Box>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{ mb: 3, maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cantidad
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {detalles.map((d, idx) => (
                    <TableRow key={`${d.nroIdformulario}-${d.nroItem}-${idx}`}>
                      <TableCell>{d.codigoArticulo || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {d.descripcionArticulo || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}
                      </TableCell>
                      <TableCell>{d.notas || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <NumeroInternoMeta nro={remito.nroFormulario} />
        </CardContent>
      </Card>

      <Dialog
        open={dialogFacturasOpen}
        onClose={() => setDialogFacturasOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Facturas asociadas</DialogTitle>
        <DialogContent>
          <Stack spacing={0.75} sx={{ pt: 0.5 }}>
            {facturasAsociadas.map((f) => (
              <Link
                key={f.nroFormulario}
                component={RouterLink}
                to={`/compras/facturas/${f.nroFormulario}`}
                underline="hover"
                onClick={() => setDialogFacturasOpen(false)}
                sx={{ color: '#1976d2', fontWeight: 500 }}
              >
                {formatoLetraSucNro(f.letra, f.nroSuc, f.nroComprobante ?? undefined)}
              </Link>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogFacturasOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ImprimirEtiquetasCodigosDialog
        open={etiquetasOpen}
        nroFormulario={remito.nroFormulario}
        detalles={detalles}
        textoOrigen="el remito"
        fileName={`etiquetas-rc-${remito.nroFormulario}.pdf`}
        obtenerPdf={(id, items, idCompania) => remitoCompraService.getEtiquetasCodigosPdf(id, items, idCompania)}
        onClose={() => setEtiquetasOpen(false)}
        onPdfReady={(url, fileName) => {
          setPdfEtiquetas((prev) => {
            if (prev?.url) URL.revokeObjectURL(prev.url);
            return { url, fileName };
          });
        }}
      />

      {pdfEtiquetas ? (
        <PDFViewer
          pdfUrl={pdfEtiquetas.url}
          title={`Etiquetas remito ${formatoLetraSucNro(remito.letra, remito.nroSuc, nroComprobanteTxt)}`}
          documentId={String(remito.nroFormulario)}
          documentType="informeCliente"
          downloadFileName={pdfEtiquetas.fileName}
          onClose={() => {
            URL.revokeObjectURL(pdfEtiquetas.url);
            setPdfEtiquetas(null);
          }}
        />
      ) : null}
    </Box>
  );
};

export default RemitoCompraDetalle;
